import { InverterAction } from '../types/global';
import { estimateEnergyCost, describeFlow, intervalValueEquation, isLowCostImport } from './utils';

// Maps live action + reason to customer-friendly what/why copy.
export type ActionInterpretation = {
  actionLabel: string;
  simpleExplanation: string;
  customerSafeReason: string;
};

// Action strings on InverterAction follow `{battery}-{solar}` — e.g.
// `discharge-export`, `charge-curtail`, `auto-curtail`.
export function parseAction(action: string): { battery: string; solar: string } {
  const parts = action?.split('-') ?? [];
  return { battery: parts[0] ?? 'unknown', solar: parts[1] ?? '' };
}

// One-word label for compact UI (timeline cells, status rows). Pair with
// interpretAction() when you need a full sentence.
export function shortLabel(battery: string): string {
  switch (battery) {
    case 'charge': return 'Charging';
    case 'discharge': return 'Discharging';
    case 'auto': return 'Auto';
    case 'stopped': return 'Idle';
    case 'fullstop': return 'Stopped';
    case 'export': return 'Exporting';
    case 'import': return 'Importing';
    default: return battery || 'Unknown';
  }
}

// Single entry point for turning an action + reason into customer copy.
//
// `userReason` is the backend-generated, customer-facing reason
// (decision_logger.get_user_reason(), surfaced on the action values). When set
// it normally wins for `customerSafeReason` — this covers both the auto-tuned decision
// engine and hacker-mode users who set their own reason. When absent (e.g. a
// hacker-mode script that never touched `decisions`), we fall back to deriving
// the copy locally from the action + raw reason string. The `actionLabel` /
// `simpleExplanation` are always derived locally so the headline stays in sync
// with the cells regardless of the override.
export function interpretAction(
  action: string,
  reason: string,
  sellPrice?: number,
  feedInLimit?: number | null,
  userReason?: string | null,
  decisionLog?: string | null,
): ActionInterpretation {
  const p2p = isP2PExport(action, decisionLog || reason);
  const base = deriveInterpretation(action, reason, sellPrice, feedInLimit, p2p);
  // These old generated templates infer the trading reason solely from a
  // numeric limit. Keep custom reasons, but replace this known boilerplate
  // with the requested-limit wording when no P2P provenance is available.
  const legacyLimitReason = typeof userReason === 'string' && (
    /^Your battery is exporting energy back to the grid, spreading it evenly at [\d.]+kW to get the best return from what's available\.$/.test(userReason.trim())
    || userReason.trim() === "Grid prices are high right now. Powston is exporting a little extra to make sure your home doesn't risk buying any expensive energy."
  );
  if (legacyLimitReason && (p2p || base.simpleExplanation.startsWith('Requested grid export limit:'))) return base;
  if (typeof userReason === 'string' && userReason.trim())
    return { ...base, customerSafeReason: userReason.trim() };
  return base;
}

// Historical values may serialize the decision object a second time. Accept
// both representations and only return the string fields used for UI copy.
export function decisionContext(value: unknown): { log?: string; user_reason?: string } {
  let parsed = value;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return {}; }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const fields = parsed as Record<string, unknown>;
  return {
    log: typeof fields.log === 'string' ? fields.log : undefined,
    user_reason: typeof fields.user_reason === 'string' ? fields.user_reason : undefined,
  };
}

function isP2PExport(action: string, reason: string): boolean {
  const exporting = /^(export(?:100|_p2p)?|forced_export|discharge-export(?:100)?)(?:-|$)/i.test(action || '');
  if (!exporting) return false;
  // A rejected or superseded P2P decision can still appear in the log. Only
  // its final marker establishes provenance for vendor-normalised commands.
  const final = (reason || '').match(/(?:^|\|)\s*final:([\w-]+)\s*$/i)?.[1];
  return (final ?? action).split('-')[0].toLowerCase() === 'export_p2p';
}

function deriveInterpretation(
  action: string,
  reason: string,
  sellPrice?: number,
  feedInLimit?: number | null,
  p2p = false,
): ActionInterpretation {
  const a = (action || '').toLowerCase();
  const r = (reason || '').toLowerCase();
  const has = (...keys: string[]) => keys.some(k => r.includes(k));

  if (p2p)
    return {
      actionLabel: 'Exporting to the grid for P2P',
      simpleExplanation: 'Powston is requesting export to honour your P2P pledge.',
      customerSafeReason: 'Your P2P pledge can start an export that would otherwise wait. It is a minimum commitment, not a ceiling on total export: other export decisions can request more. Actual delivery depends on the system response; matched earnings are confirmed by settlement.',
    };

  // The explicit defensive-export command carries intent; a small numeric
  // limit alone does not (it could also have been raised to a P2P floor).
  if (a.includes('export100'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Pushing a little extra to the grid. Avoiding high buy prices.', customerSafeReason: 'Grid prices are high right now. Powston is exporting a little extra to make sure your home doesn\'t risk buying any expensive energy.' };

  // A requested control limit is not proof of measured grid export.
  if (a.includes('export') && feedInLimit != null && Number.isFinite(feedInLimit) && feedInLimit >= 0)
    return { actionLabel: 'Exporting to the grid', simpleExplanation: `Requested grid export limit: ${feedInLimit / 1000} kW.`, customerSafeReason: `Powston requested a grid export limit of ${feedInLimit / 1000} kW. This is a control setting; check the interval readings for actual export.` };

  // export at modest sell price (< 30¢) — extra capacity, selling at best available to reduce daily costs
  if (a.includes('export') && sellPrice !== undefined && sellPrice < 0.30)
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Selling at the best price available.', customerSafeReason: 'Your battery has extra capacity. Powston is selling at the best price available to lower your daily costs.' };

  if (a.includes('export') && has('max price', 'high now', 'peak sell', 'profitable margin', 'good price'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Exporting to the grid. Sell price is high.', customerSafeReason: 'Your battery is selling energy back to the grid. You\'re earning money right now.' };

  if (a.includes('import_no_solar') && has('negative prices', 'negative wholesale', 'negative price'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Energy is better than free right now.', customerSafeReason: 'Buying energy is getting you paid. Powston buying as much as possible.' };

  if (a.includes('import') && has('negative prices', 'negative wholesale', 'negative price'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Energy is near forecast low right now.', customerSafeReason: 'Energy is near forecast low right now. Powston is storing it to sell later at a profit.' };

  if (a.includes('charge') && has('charging from solar'))
    return { actionLabel: 'Charging from solar', simpleExplanation: 'Charging from solar. Storing free energy for later.', customerSafeReason: 'Your panels are generating free energy. Powston is storing it for when it\'s worth more.' };

  if (a.includes('charge') && has('negative wholesale'))
    return { actionLabel: 'Charging the battery', simpleExplanation: 'Charging the battery while energy prices are low.', customerSafeReason: 'Energy prices are low. Powston is storing energy for later.' };

  if (has('curtailing all exports', 'export price is negative'))
    return { actionLabel: 'Standing by', simpleExplanation: 'Standing by. Waiting for the right moment to sell.', customerSafeReason: 'Prices are low right now. Powston is waiting for the right moment to sell.' };

  if (a.includes('export') && has('dump before the price goes negative', 'morning dumping'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Exporting to the grid. Making room for cheaper energy later.', customerSafeReason: 'Powston is making room in your battery for cheaper energy later today.' };

  if (a.includes('import') && has('low soc', 'before evening peak', 'protect battery', 'topping up'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Topping up before prices rise.', customerSafeReason: 'Powston is topping up your battery before energy prices rise tonight.' };

  if (a.includes('auto') && has('waiting for more solar', 'strong solar forecast', 'delaying import'))
    return { actionLabel: 'Waiting for solar', simpleExplanation: 'Solar is on the way. Saving space for free energy.', customerSafeReason: 'Solar is forecast for later. Powston is saving space in your battery for free energy.' };

  if (a.includes('auto') && has('better sell opportunities', 'conserving remaining battery', 'conserving battery'))
    return { actionLabel: 'Holding', simpleExplanation: 'Holding energy for a better price later.', customerSafeReason: 'Powston is holding your stored energy for a better price later today.' };

  // Fallbacks by action
  if (a.includes('export'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Exporting to the grid. Earning money right now.', customerSafeReason: 'Your battery is selling energy back to the grid. You\'re earning money right now.' };
  if (a.includes('import'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Topping up while energy is affordable.', customerSafeReason: 'Powston is topping up your battery while energy is affordable.' };
  // Discharge must come before charge — "discharge" contains the substring
  // "charge", so a.includes('charge') would otherwise swallow every discharge.
  if (a.includes('discharge'))
    return { actionLabel: 'Discharging', simpleExplanation: 'Your battery is powering your home.', customerSafeReason: 'Your battery is powering your home and reducing your bill.' };
  if (a.includes('charge'))
    return { actionLabel: 'Charging from solar', simpleExplanation: 'Charging from solar. Storing free energy for later.', customerSafeReason: 'Your battery is charging from solar. Free energy, stored for later.' };
  if (a.includes('auto'))
    return { actionLabel: 'Optimising', simpleExplanation: 'Watching the market, ready to act.', customerSafeReason: 'Powston is watching the market, ready to act.' };

  return { actionLabel: 'Monitoring', simpleExplanation: 'Watching the market, ready to act.', customerSafeReason: 'Powston is watching the market, ready to act.' };
}

// Easy, customer-safe one-liner for an interval's reason — the plain-language
// "why" to show on summary/display surfaces, with the raw reason + full
// decision logic reserved for drill-down. Centralises the values parsing so
// callers don't each re-derive sell price / feed-in limit.
export function customerReason(action: InverterAction): string {
  const parsed = (() => { try { return JSON.parse(action.values); } catch { return {}; } })();
  const decisions = decisionContext(parsed?.decisions);
  const sellPriceCents = action.sell_price ?? parsed?.sell_price ?? 0;
  const feedInLimit = parsed?.feed_in_power_limitation ?? null;
  // Prefer the backend-generated reason; fall back to local interpretation.
  const backendUserReason = parsed?.user_reason ?? decisions.user_reason ?? null;
  const solarPower = parsed?.solar_power ?? parsed?.ppv;
  const falseSolarReason = typeof backendUserReason === 'string'
    && backendUserReason.includes('Your panels are generating free energy')
    && typeof solarPower === 'number'
    && solarPower <= 0;
  const userReason = falseSolarReason ? null : backendUserReason;
  return interpretAction(action.action, action.reason, sellPriceCents / 100, feedInLimit, userReason, decisions.log).customerSafeReason;
}

// Command bases that name a grid direction, matched exactly rather than by
// substring. `solar_export` is the reason this is an allowlist: it contains
// "export" but is a solar-handling instruction, so if the house out-draws solar
// the site still imports — and a substring match would announce a dispatch
// failure on all 1728 of the intervals the fleet records in a typical 3 days.
// Every entry here is a base observed in production; the only real suffixes are
// `-curtail` and `-maximize`, both solar decisions (see `check_curtail` and
// `interval_detail._is_passive_command` in the backend).
const EXPORT_COMMANDS = new Set(['export', 'export100', 'export_p2p', 'forced_export']);
const IMPORT_COMMANDS = new Set(['import', 'import_no_solar', 'import_at_max', 'forced_import']);

// The grid direction a commanded action asks for, when it asks for one at all.
// `null` means the command makes no claim about which way power crosses the
// meter — `auto` hands control back to the inverter, and charge/discharge are
// satisfied from solar or the house just as well as from the grid — so measured
// flow in either direction is consistent with it and must not be reported as a
// contradiction. Only `export`/`import` name a grid direction outright.
export function commandedGridDirection(action: string): 'Export' | 'Import' | null {
  const battery = parseAction((action || '').toLowerCase()).battery;
  if (EXPORT_COMMANDS.has(battery)) return 'Export';
  if (IMPORT_COMMANDS.has(battery)) return 'Import';
  return null;
}

// The grid direction the *rendered headline* names, if any.
//
// Distinct from commandedGridDirection() on purpose. deriveInterpretation()
// matches on the whole action string, so the solar half reaches it too:
// `discharge-export` yields the label "Exporting to the grid" even though the
// command's base names no grid direction. That headline still contradicts a
// measured import, so it has to be re-framed as a request — but it must not
// carry the "the battery did not carry out the export" accusation, which stays
// gated on commandedGridDirection() because the solar half never asked the
// battery for a grid direction in the first place.
//
// Derived from the label rather than the action so it cannot drift from what
// the user actually reads; only these two labels name a grid direction.
export function headlineGridDirection(actionLabel: string): 'Export' | 'Import' | null {
  if (actionLabel.startsWith('Exporting')) return 'Export';
  if (actionLabel.startsWith('Importing')) return 'Import';
  return null;
}

// Last-resort headline for an interval whose command names no grid direction
// but whose every derived label does (`solar_export`). It asserts nothing the
// meter could contradict and hands the reader straight to the figures.
const NON_DIRECTIONAL_FALLBACK = {
  actionLabel: 'Powston was managing your system',
  // Deliberately names no source: this is used on estimated intervals too,
  // where "what your meter recorded" would be untrue.
  simpleExplanation: 'The figures below are what was recorded for this interval.',
};


// Plain-language interpretation of a single interval — answers "does this make
// sense?" without making the user do the maths. Pure (no JSX) so the desktop
// table and mobile card render the identical text. Reuses interpretAction for
// the headline and the shared describeFlow/formatIntervalValue helpers so the
// wording can't contradict the cells.
//
// `outcomeLine` is set only when what Powston commanded and what the meter
// recorded disagree. The headline is derived from the command and the energy
// figures from the meter, so on a mismatch the card would otherwise assert
// "Exporting to the grid. Earning money right now." directly above "Imported
// 1.2 kWh" — two true-in-isolation statements that read as a broken display and
// hide the disagreement rather than surface it. On a mismatch the headline is
// re-framed as a request rather than an outcome.
//
// Two gates keep the wording honest:
//  - the headline is re-framed whenever the direction it names disagrees with
//    the figures under it, including for `discharge-export`/`solar_export`
//    actions whose *command* names no grid direction but whose *label* does;
//  - settled data may be called meter evidence; estimated data is attributed to
//    the inverter's own readings.
//
// What it deliberately does NOT do is judge whether the hardware complied.
export type IntervalExplanation = {
  headline: string;
  energyLine: string;
  valueLine: string;
  outcomeLine: string;
  verificationLine: string;
  rounded: boolean;
  isSettled: boolean;
  isFlowMetered: boolean;
};

// The single classification every piece of interval copy is derived from.
//
// Three independent signals bear on an interval — the direction the *command*
// named, the direction the generated *copy* names, and the direction the meter
// *measured* — plus corroboration from battery power and SOC. Deciding the
// wording piecemeal from those signals is what produced a headline that
// contradicted the figures under it, an outcome line that contradicted the
// energy line, and a dispatch-failure claim built from grid flow alone. So they
// are reduced to one state here, once, and every line below is a function of it.
//
//  - `agrees`          — the meter matches the direction on screen.
//  - `contradicted`    — the meter points a different way from the direction the
//                        card names. A statement about the two readings, NOT a
//                        verdict on the hardware.
//
//  - `bidirectional`   — the meter recorded both import and export. Neither
//                        an exclusive direction nor Idle describes it.
//
// Deliberately absent: any judgement about whether the inverter obeyed. That
// needs battery telemetry, the site's configured minimum SOC, the gross meter
// channels and settled data together — `sanity_dog` already holds all four.
// Adjudicating it from a subset here produced both false failure claims and,
// worse, false successes. The card's job is to show what was asked and what
// happened.
export type IntervalOutcomeState = 'agrees' | 'contradicted' | 'bidirectional';

export type IntervalClassification = {
  state: IntervalOutcomeState;
  /** Direction the command named, or null when it named none. */
  commanded: 'Export' | 'Import' | null;
  /** Direction the card is permitted to name; null means it may name none. */
  claimed: 'Export' | 'Import' | null;
  /** The observed flow does not support an exclusive directional headline. */
  contradictsClaim: boolean;
  isSettled: boolean;
  flow: ReturnType<typeof describeFlow>;
};

export function classifyInterval(action: InverterAction, actionLabel: string): IntervalClassification {
  const flow = describeFlow(action);
  const isSettled = action.quality === 'Act';
  const parsed = (() => { try { return JSON.parse(action.values); } catch { return {}; } })();
  const reason = decisionContext(parsed?.decisions).log || action.reason;
  // Recover only confirmed P2P provenance, without changing the recorded
  // action or the meter/telemetry fields used to describe measured flow.
  const commanded = isP2PExport(action.action, reason) ? 'Export' : commandedGridDirection(action.action);
  // A direction may be asserted only if some signal supports it. The command is
  // the strong signal; the copy is the weak one and can never license a
  // dispatch claim, only oblige us to stop asserting its direction.
  const claimed = commanded ?? headlineGridDirection(actionLabel);
  const contradictsClaim = claimed !== null && flow.word !== claimed;
  const base = { commanded, claimed, isSettled, contradictsClaim, flow };
  return { ...base, state: flow.word === 'Bidirectional' ? 'bidirectional' : contradictsClaim ? 'contradicted' : 'agrees' };
}

export function buildIntervalExplanation(action: InverterAction): IntervalExplanation {
  const parsed = (() => { try { return JSON.parse(action.values); } catch { return {}; } })();
  const sellPriceCents = action.sell_price ?? parsed?.sell_price ?? 0;
  const feedInLimit = parsed?.feed_in_power_limitation ?? null;

  const interp = interpretAction(action.action, action.reason, sellPriceCents / 100, feedInLimit, undefined, decisionContext(parsed?.decisions).log);
  const c = classifyInterval(action, interp.actionLabel);
  const { state, commanded, isSettled, flow } = c;

  // ── Headline ──────────────────────────────────────────────────────────
  // Only `agrees` may keep a directional assertion. Otherwise: a real grid
  // command is re-framed as the request it was, and a direction that came only
  // from the copy is dropped entirely — including when stripping the solar
  // suffix leaves a base that is itself directional (`solar_export`).
  let headline: string;
  if (!c.contradictsClaim) {
    headline = `${interp.actionLabel}. ${interp.simpleExplanation}`;
  } else if (commanded !== null) {
    headline = `Powston asked for: ${interp.actionLabel}.`;
  } else {
    const baseInterp = interpretAction(parseAction(action.action).battery, action.reason, sellPriceCents / 100, feedInLimit);
    headline = headlineGridDirection(baseInterp.actionLabel) === null
      ? `${baseInterp.actionLabel}. ${baseInterp.simpleExplanation}`
      : `${NON_DIRECTIONAL_FALLBACK.actionLabel}. ${NON_DIRECTIONAL_FALLBACK.simpleExplanation}`;
  }

  // ── Outcome ───────────────────────────────────────────────────────────
  // A juxtaposition of two facts — what Powston commanded, and what the meter
  // recorded — and nothing more. No causal claim, and above all no verdict on
  // whether the hardware complied: see the note on IntervalOutcomeState.
  //
  // `source` keeps estimated data from ever being worded as meter evidence.
  const source = flow.isMetered ? 'Your meter recorded' : 'Your inverter\'s readings show';
  const observed = flow.word === 'Idle'
    ? flow.idleText.replace(' this interval', '').toLowerCase()
    : flow.legs.map(leg => `an ${leg.word.toLowerCase()} of ${leg.kwh}`).join(' and ');
  const commandBase = parseAction((action.action || '').toLowerCase()).battery || 'no';

  let outcomeLine = '';
  if (state === 'bidirectional') {
    // The energy lines already give both magnitudes. Keep this caption short
    // enough for the narrow desktop column, without implying a dispatch verdict.
    outcomeLine = `${source} both import and export this interval.`;
  } else if (state !== 'agrees' && commanded !== null) {
    outcomeLine = `Powston commanded ${commanded.toLowerCase()}. ${source} ${observed} this interval.`;
  } else if (state !== 'agrees' && c.claimed !== null) {
    // The command named no grid direction at all, so the two readings are not
    // even in tension — say so plainly rather than leaving the reader to guess.
    outcomeLine = `Powston commanded ${commandBase}, which does not set a grid direction. ${source} ${observed} this interval.`;
  }

  // ── Energy and value ──────────────────────────────────────────────────
  const v = estimateEnergyCost(action); // dollars; negative = profit (supply charge shown separately)

  let energyLine: string;
  let valueLine = `${intervalValueEquation(action)}.`;
  if (flow.word === 'Idle') {
    energyLine = `${flow.idleText}.`;
  } else if (flow.word === 'Bidirectional') {
    energyLine = flow.legs.map(leg => `${leg.word === 'Export' ? 'Exported' : 'imported'} ${leg.kwh}`).join(' and ') + ' this interval.';
  } else {
    const leg = flow.legs[0];
    const verb = flow.word === 'Export' ? 'Exported' : 'Imported';
    energyLine = isLowCostImport(action)
      ? `Grid import: ${leg.kwh} this interval.`
      : `${verb} ${leg.kwh} this interval (about ${leg.kw} for these 5 minutes).`;
  }

  // A small non-zero financial value needs its rounding explained even when
  // the meter channels cancel or no measurable flow is available.
  const rounded = v !== 0 && Math.abs(v) < 0.005;
  if (rounded) {
    valueLine += ' The value is under half a cent for this interval, so the displayed amount is rounded.';
  }

  const verificationLine = isSettled
    ? flow.isMetered
      ? 'Settled against your meter data. Retailers can still revise "actual" figures until after midday the following day, so treat this as near-final until then.'
      : 'Financial value uses settled retailer charges. Grid flow is estimated from inverter readings; meter energy readings are unavailable.'
    : 'Estimated from inverter readings. The figure firms up once your retailer sends meter data — from around midday the day after, and it can keep settling for a little while after that.';

  return { headline, energyLine, valueLine, outcomeLine, verificationLine, rounded, isSettled, isFlowMetered: flow.isMetered };
}
